function initProductV2(container) {
  if (!container) return;

  const sectionId = container.id.replace('MainProduct-', '');
  const jsonElement = document.getElementById(`ProductJSON-${sectionId}`);
  if (!jsonElement) return;
  const variants = JSON.parse(jsonElement.textContent);

  const getSydneyDate = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Australia/Sydney',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
      hour12: false
    }).formatToParts(new Date());
    const map = {};
    parts.forEach(({ type, value }) => map[type] = value);
    return new Date(map.year, map.month - 1, map.day, map.hour, map.minute, map.second);
  };

  /* Limited edition drop countdown.
     DROP_HOUR is the only place the hour of a drop is set; 0 lands it at midnight
     Sydney on the date in the variant's custom.drop_date metafield.
     The deadline is one fixed timestamp and is never recomputed from "now": a
     countdown that restarts on reload is misleading conduct under Australian
     Consumer Law, so that is a requirement rather than a preference. */
  const DROP_HOUR = 0;

  // Parses a drop_date into Sydney wall-clock fields. Parsed by hand because
  // new Date(str) reads a bare ISO date as UTC, landing the deadline on the
  // wrong side of midnight.
  const getDropDeadline = (dropDateStr) => {
    if (!dropDateStr) return null;
    const parts = String(dropDateStr).split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!year || !month || !day) return null;
    const deadline = new Date(year, month - 1, day, DROP_HOUR, 0, 0, 0);
    return isNaN(deadline.getTime()) ? null : deadline;
  };

  // The one place "is this dropping?" is answered, so both pickers and every bundle
  // piece apply the same rule. Returns the deadline only while it is still ahead.
  const futureDropDeadline = (subject) => {
    if (!subject) return null;
    const raw = typeof subject === 'string' ? subject : subject.drop_date;
    const deadline = getDropDeadline(raw);
    return deadline !== null && deadline > getSydneyDate() ? deadline : null;
  };

  // The later of two deadlines. A pair or bundle is not complete until its LAST
  // piece has dropped, so that is the date the customer is waiting on.
  const laterDeadline = (a, b) => {
    if (!a) return b || null;
    if (!b) return a;
    return a > b ? a : b;
  };

  // 7d 14:06:41 while more than a day out, then 14:06:41, then 06:41 in the final
  // hour. Seconds show at every scale deliberately, matching the in-stock shipping
  // line; do not drop them above an hour.
  const formatDropRemaining = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    return `${pad(minutes)}:${pad(seconds)}`;
  };

  // Dynamic day price initialisation
  const dayPriceEl = container.querySelector('.js-day-price');
  if (dayPriceEl) {
    const sydneyDate = getSydneyDate();
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    dayPriceEl.textContent = `${dayNames[sydneyDate.getDay()]} Price`;
  }

  // Variant availability evaluation
  function updateAvailability() {
    const pickers = container.querySelectorAll('.product-v2__variant-picker');
    if (!pickers.length) return;

    pickers.forEach(picker => {
      const fieldsets = Array.from(picker.querySelectorAll('fieldset.product-v2__option-fieldset'));
      
      const currentSelections = fieldsets.map(fieldset => {
        const checked = fieldset.querySelector('.js-variant-radio:checked');
        return checked ? checked.value : null;
      });

      fieldsets.forEach((fieldset, optionIndex) => {
        const radios = fieldset.querySelectorAll('.js-variant-radio');
        
        radios.forEach(radio => {
          const hypotheticalSelections = [...currentSelections];
          hypotheticalSelections[optionIndex] = radio.value;
          
          const matchingVariant = variants.find(v => {
            return hypotheticalSelections.every((val, i) => val === null || v.options[i] === val);
          });
          
          let isAvailable = false;
          if (matchingVariant) {
            isAvailable = matchingVariant.available || matchingVariant.inventory_policy === 'continue';
          }
          
          const label = picker.querySelector(`label[for="${radio.id}"]`);
          if (label) {
            if (isAvailable) {
              label.classList.remove('is-disabled');
            } else {
              label.classList.add('is-disabled');
            }
          }
        });
      });

      const currentVariant = variants.find(v => {
        return currentSelections.every((val, i) => val === null || v.options[i] === val);
      });

      let isCurrentAvailable = false;
      if (currentVariant) {
        isCurrentAvailable = currentVariant.available || currentVariant.inventory_policy === 'continue';
      }

      if (!isCurrentAvailable && currentSelections.every(v => v !== null)) {
        picker.classList.add('has-error');
      } else {
        picker.classList.remove('has-error');
      }
    });
  }
  
  // Flickity gallery initialisation
  const sliderElem = container.querySelector('.product__image-slider');
  const thumbElem = container.querySelector('.product__image-thumbnail');

  if (sliderElem && typeof Flickity !== 'undefined') {
    const flkty = new Flickity(sliderElem, {
      imagesLoaded: true,
      wrapAround: true,
      pageDots: false,
      prevNextButtons: true,
      adaptiveHeight: true
    });

    if (thumbElem) {
      new Flickity(thumbElem, {
        asNavFor: sliderElem,
        contain: true,
        pageDots: false,
        prevNextButtons: false
      });
    }

    container._flktyV2 = flkty;
  }

  // Variant selection handler
  container.addEventListener('change', function(e) {
    if (e.target.matches('.js-variant-radio')) {
      const fieldset = e.target.closest('fieldset');
      if (fieldset) {
        const selectedValueSpan = fieldset.querySelector('.js-selected-value');
        if (selectedValueSpan) selectedValueSpan.textContent = e.target.value;
      }

      const pickers = container.querySelectorAll('.product-v2__variant-picker');
      
      const v1Options = Array.from(pickers[0].querySelectorAll('.js-variant-radio:checked')).map(r => r.value);
      const v1 = variants.find(v => v1Options.every((val, i) => v.options[i] === val));
      
      let v2 = null;
      if (pickers.length > 1) {
        const v2Options = Array.from(pickers[1].querySelectorAll('.js-variant-radio:checked')).map(r => r.value);
        v2 = variants.find(v => v2Options.every((val, i) => v.options[i] === val));
      }

      if (v1) updateProductUI(v1, v2, container);
      updateAvailability();
    }
  });

  // Master UI update logic
  function updateProductUI(variant, v2, container) {
    const idInput = container.querySelector('.product-variant-id');
    if (idInput) idInput.value = variant.id;
    window.history.replaceState({}, '', `?variant=${variant.id}`);

    const klaviyoSlot = container.querySelector('.klaviyo-favorites-pdp-slot');
    if (klaviyoSlot && variant) {
      klaviyoSlot.setAttribute('data-variant-id', variant.id);
      if (window.klaviyo && window.klaviyo.refreshVariant) {
        window.klaviyo.refreshVariant(variant.id);
      }
    }

    let displayVariant = variant;
    if (v2 && v2.price > variant.price) {
      displayVariant = v2;
    }

    const priceEls = container.querySelectorAll('.js-price');
    const saveEl = container.querySelector('.js-save-price');
    const compEl = container.querySelector('.js-compare-price');

    const formatPrice = (cents) => cents % 100 === 0 ? (cents / 100).toString() : (cents / 100).toFixed(2);

    const priceValue = formatPrice(displayVariant.price);
    const formattedPriceHTML = `<sup class="price-symbol">$</sup>${priceValue}`;
    
    priceEls.forEach(el => {
      el.innerHTML = formattedPriceHTML;
    });

    const saveBadge = container.querySelector('.js-save-badge');
    const compareBadge = container.querySelector('.js-compare-badge');

    if (displayVariant.compare_at_price > displayVariant.price) {
      if (saveEl) saveEl.textContent = `$${formatPrice(displayVariant.compare_at_price - displayVariant.price)}`;
      if (compEl) compEl.textContent = `$${formatPrice(displayVariant.compare_at_price)}`;
      if (saveBadge) saveBadge.classList.remove('is-hidden');
      if (compareBadge) compareBadge.classList.remove('is-hidden');
    } else {
      if (saveBadge) saveBadge.classList.add('is-hidden');
      if (compareBadge) compareBadge.classList.add('is-hidden');
    }

    const afterpayEl = container.querySelector('.js-afterpay-payments');
    if (afterpayEl) {
      const afterpayCents = Math.round(displayVariant.price / 4);
      afterpayEl.textContent = `$${formatPrice(afterpayCents)}`;
    }

    if (container._flktyV2 && variant.featured_media && variant.featured_media.id !== 0) {
      const slides = Array.from(container.querySelectorAll('.product__image-slider .carousel-cell'));
      const targetIndex = slides.findIndex(s => parseInt(s.dataset.mediaId) === variant.featured_media.id);
      if (targetIndex !== -1) container._flktyV2.select(targetIndex);
    }

    // Availability state flags
    let isPreorder = false;
    let isBackorder = false;
    const isOOS = variant.inventory_quantity <= 0;
    const continueSelling = variant.inventory_policy === 'continue';
    const hasDispatchDate = variant.estimated_dispatch !== null && variant.estimated_dispatch !== '';

    // Buyability: in stock, or out of stock and still selling. The same rule is written
    // in three places - here, can_add in the Liquid and isBuyable() in the bundle - and
    // every pre-order state below must stay gated on it, or a drop_date on a deny
    // variant offers a Pre-Order the cart then refuses.
    const v1Buyable = variant.available || continueSelling;
    const v2Buyable = !v2 || v2.available || v2.inventory_policy === 'continue';
    const selectionBuyable = v1Buyable && v2Buyable;

    // A future drop date triggers pre-order on its own, so a merchandiser fills in
    // EITHER estimated dispatch OR a drop date, never both. Gated here at the source
    // so button, delivery row and countdown all inherit it.
    const dropDeadline = selectionBuyable ? futureDropDeadline(variant) : null;
    const hasFutureDrop = dropDeadline !== null;

    // The second picker follows the same rule. A pair is not complete until the LATER
    // of the two has landed, so that is the date the countdown runs to.
    const v2DropDeadline = selectionBuyable ? futureDropDeadline(v2) : null;
    const effectiveDropDeadline = laterDeadline(dropDeadline, v2DropDeadline);
    const hasAnyFutureDrop = effectiveDropDeadline !== null;

    if (selectionBuyable && (hasFutureDrop || (isOOS && continueSelling && hasDispatchDate))) {
      isPreorder = true;
    } else if (isOOS && continueSelling && !hasDispatchDate) {
      isBackorder = true;
    }
    
    let isComingSoon = (!variant.available && variant.coming_soon);
    
    const btn = container.querySelector('.addToCartBtn');
    
    // Cart button state logic
    if (btn) {
      const btnLabel = btn.querySelector('.js-atc-label') || btn.querySelector('span');
      const btnPriceWrapper = btn.querySelector('.js-atc-price-wrapper');
      const btnPrice = btn.querySelector('.js-atc-price');
      
      btn.classList.remove('preorder', 'disabled', 'oos');
      btn.disabled = false;

      let v2Unavailable = false;
      let v2Preorder = false;
      if (v2) {
        if (!v2Buyable) v2Unavailable = true;

        const v2OOS = v2.inventory_quantity <= 0;
        const v2ContinueSelling = v2.inventory_policy === 'continue';
        const v2HasDispatchDate = v2.estimated_dispatch !== null && v2.estimated_dispatch !== '';

        // Same gate: an unbuyable v2 must not clear v2Unavailable.
        if (selectionBuyable && (v2DropDeadline || (v2OOS && v2ContinueSelling && v2HasDispatchDate))) {
           v2Preorder = true;
           v2Unavailable = false;
        }
      }

      if (isPreorder || v2Preorder) {
        btn.classList.add('preorder');
        if (btnLabel) btnLabel.textContent = v2 ? "Pre-Order Both" : "Pre-Order";
        if (btnPriceWrapper) btnPriceWrapper.classList.remove('is-hidden');
      } else if (v1Buyable && !v2Unavailable) {
        if (btnLabel) btnLabel.textContent = v2 ? "Add Both to Cart" : "Add to Cart";
        if (btnPriceWrapper) btnPriceWrapper.classList.remove('is-hidden');
      } else if (isComingSoon) {
        btn.disabled = true;
        btn.classList.add('disabled');
        if (btnLabel) btnLabel.textContent = "Coming Soon!";
        if (btnPriceWrapper) btnPriceWrapper.classList.add('is-hidden');
      } else {
        btn.disabled = true;
        btn.classList.add('disabled', 'oos');
        
        let labelText = "WOW! THIS SOLD OUT QUICKLY";
        if (v2) {
            if (!v1Buyable && v2Unavailable) {
                labelText = "BOTH SETS OUT OF STOCK";
            } else if (!v1Buyable) {
                labelText = "FIRST SET OUT OF STOCK";
            } else if (v2Unavailable) {
                labelText = "SECOND SET OUT OF STOCK";
            } else {
                labelText = "SOLD OUT! PICK DIFFERENT SETS";
            }
        }
        
        if (btnLabel) btnLabel.textContent = labelText;
        if (btnPriceWrapper) btnPriceWrapper.classList.add('is-hidden');
      }

      if (btnPrice) {
        btnPrice.textContent = `$${formatPrice(displayVariant.price)}`;
      }
    }

    const sisterFooters = container.querySelectorAll('.sister-product__card-footer[data-sibling-price]');
    sisterFooters.forEach(footer => {
      const siblingPrice = parseInt(footer.dataset.siblingPrice, 10);
      const diff = siblingPrice - displayVariant.price;
      
      let diffHtml = '';
      if (diff > 0) {
        diffHtml = `<div class="sister-product__price-diff positive">+$${formatPrice(diff)}</div>`;
      } else if (diff < 0) {
        diffHtml = `<div class="sister-product__price-diff negative">-$${formatPrice(Math.abs(diff))}</div>`;
      }
      footer.innerHTML = diffHtml;
    });

    // Dynamic shipping text routing
    const shippingTextEl = container.querySelector('.js-shipping-text');
    const shippingRow = container.querySelector('.js-dynamic-shipping');
    
    if (shippingTextEl && shippingRow) {
      const dispatchTime = variant.estimated_dispatch || 'upcoming';
      
      const isAvailable = selectionBuyable;

      container._currentPrice = displayVariant.price;
      const shippingPrefix = displayVariant.price < 14900 ? "Free Shipping over $149" : "Free Shipping";

      shippingRow.classList.remove('is-preorder', 'is-coming-soon', 'is-oos', 'is-in-stock', 'is-backorder', 'is-drop');

      // A drop_date still in the future overrides estimated_dispatch and every
      // other state below it, and takes the delivery line for itself. Stash the
      // variant so updateDropTimer() can hand the row back when it passes.
      container._dropDeadline = effectiveDropDeadline;
      container._dropVariant = variant;
      container._dropV2 = v2;

      if (hasAnyFutureDrop) {
        shippingRow.classList.add('is-drop');
        shippingTextEl.innerHTML = `<span class="dynamic-info__highlight">Limited Edition Colour</span> <span class="dynamic-info__text">dropping in <span class="dynamic-info__bold js-drop-timer-target"></span></span>`;
        updateDropTimer();
      } else if (isPreorder) {
        shippingRow.classList.add('is-preorder');
        shippingTextEl.innerHTML = `<span class="dynamic-info__highlight">Pre-order</span> <span class="dynamic-info__text">now to reserve yours for our <span class="dynamic-info__bold">${dispatchTime}</span> shipment.</span>`;
      } else if (isBackorder) {
        shippingRow.classList.add('is-backorder');
        shippingTextEl.innerHTML = `<span class="dynamic-info__highlight">${shippingPrefix}:</span> <span class="dynamic-info__text">Order now for secure, tracked delivery.</span>`;
      } else if (isComingSoon) {
        shippingRow.classList.add('is-coming-soon');
        shippingTextEl.innerHTML = `<span class="dynamic-info__text">It's on the way! Check back to grab yours.</span>`;
      } else if (!isAvailable) {
        shippingRow.classList.add('is-oos');
        shippingTextEl.innerHTML = `<span class="dynamic-info__oos-highlight">Out of stock:</span> <span class="dynamic-info__text">Join the waitlist or check back soon to catch the next drop.</span>`;
      } else {
        shippingRow.classList.add('is-in-stock');
        shippingTextEl.innerHTML = `<span class="js-shipping-timer-target"></span>`;
        updateShippingTimer(); 
      }

      if (!shippingRow.classList.contains('is-ready')) {
        setTimeout(() => {
          shippingRow.classList.add('is-ready');
        }, 50);
      }
    }
  }
    
  // Dynamic shipping timer & intersection observer
  let shippingInterval;

  function updateShippingTimer() {
    const timerTargets = container.querySelectorAll('.js-shipping-timer-target');
    const shippingRow = container.querySelector('.js-dynamic-shipping');

    if (timerTargets.length === 0 || !shippingRow || !shippingRow.classList.contains('is-in-stock')) return;

    const now = getSydneyDate();
    let target = new Date(now);

    let shipDayText = "";
    const day = now.getDay(); 
    const hour = now.getHours();

    const overrideDateStr = container.dataset.dispatchOverride;
    let isOverrideActive = false;

    // Holiday dispatch override logic
    if (overrideDateStr) {
      const overrideParts = overrideDateStr.split('-');
      if (overrideParts.length === 3) {
        const overrideDate = new Date(now);
        overrideDate.setFullYear(parseInt(overrideParts[0], 10));
        overrideDate.setMonth(parseInt(overrideParts[1], 10) - 1);
        overrideDate.setDate(parseInt(overrideParts[2], 10));
        overrideDate.setHours(12, 0, 0, 0); 

        if (now < overrideDate) {
          target = overrideDate;
          shipDayText = "next-business-day dispatch";
          isOverrideActive = true;
        }
      }
    }

    // Standard dispatch logic
    if (!isOverrideActive) {
      if ((day === 5 && hour >= 12) || day === 6 || day === 0 || (day === 1 && hour < 12)) {
        if (day === 5) target.setDate(target.getDate() + 3);      
        else if (day === 6) target.setDate(target.getDate() + 2); 
        else if (day === 0) target.setDate(target.getDate() + 1); 

        target.setHours(12, 0, 0, 0); 
        shipDayText = "next-business-day dispatch";
      } else {
        if (hour < 12) {
          target.setHours(12, 0, 0, 0); 
          shipDayText = "same-day dispatch";
        } else {
          target.setDate(target.getDate() + 1);
          target.setHours(0, 0, 0, 0); 
          shipDayText = "next-day dispatch";
        }
      }
    }

    const diff = target - now;

    const h = String(Math.floor(diff / (1000 * 60 * 60))).padStart(2, '0');
    const m = String(Math.floor((diff / 1000 / 60) % 60)).padStart(2, '0');
    const s = String(Math.floor((diff / 1000) % 60)).padStart(2, '0');

    const currentPrice = container._currentPrice || 0;
    const shippingPrefix = (currentPrice > 0 && currentPrice < 14900) ? "Free Shipping for orders over $149" : "Free Shipping";

    const html = `<span class="dynamic-info__highlight">${shippingPrefix}:</span> <span class="dynamic-info__text">Order within <span class="dynamic-info__bold">${h}:${m}:${s}</span> for <span class="dynamic-info__bold">${shipDayText}</span></span>`;

    timerTargets.forEach(el => {
      el.innerHTML = html;
    });
  }

  function updateDropTimer() {
    const timerTargets = container.querySelectorAll('.js-drop-timer-target');
    const shippingRow = container.querySelector('.js-dynamic-shipping');
    const deadline = container._dropDeadline;

    if (timerTargets.length === 0 || !shippingRow || !deadline) return;

    const diff = deadline - getSydneyDate();

    if (diff <= 0) {
      // Dropped: clear the countdown and hand the line back to normal shipping
      // routing. It never restarts and is never left sitting on 0d 00:00.
      container._dropDeadline = null;
      if (container._dropVariant) {
        updateProductUI(container._dropVariant, container._dropV2, container);
      }
      return;
    }

    const remaining = formatDropRemaining(diff);
    timerTargets.forEach(el => {
      el.textContent = remaining;
    });
  }

  // The bundle's own countdown, under the buy button. Separate from updateDropTimer
  // because the delivery row describes the BUNDLE product's stock while this describes
  // the pieces the customer has chosen; both can be true at once.
  function updateBundleDropTimer() {
    const target = container.querySelector('.js-bundle-drop-timer-target');
    const deadline = container._bundleDropDeadline;

    if (!target || !deadline) return;

    const diff = deadline - getSydneyDate();

    if (diff <= 0) {
      // Dropped: clear the deadline and re-render, which hides the note and restores
      // the ordinary Add Bundle to Cart button. The countdown never restarts.
      container._bundleDropDeadline = null;
      if (container._bundleManager) container._bundleManager.updatePricing();
      return;
    }

    target.textContent = formatDropRemaining(diff);
  }

  // One tick drives every clock. Each returns early when its own row is not
  // showing, so only one of them ever writes on any given render.
  function updateDynamicInfoTimers() {
    updateShippingTimer();
    updateDropTimer();
    updateBundleDropTimer();
  }

  // Two elements can carry a clock: the delivery row and the bundle drop note. Run the
  // tick while ANY of them is on screen. Do NOT clear the interval per entry - with more
  // than one target, a hidden element's callback would stop a visible element's clock.
  const clockElements = [
    container.querySelector('.js-dynamic-shipping'),
    container.querySelector('.js-bundle-drop-note')
  ].filter(Boolean);

  if (clockElements.length > 0 && typeof IntersectionObserver !== 'undefined') {
    const onScreen = new Set();

    const timerObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      });

      clearInterval(shippingInterval);

      if (onScreen.size > 0) {
        updateDynamicInfoTimers();
        shippingInterval = setInterval(updateDynamicInfoTimers, 1000);
      }
    }, { threshold: 0 });

    clockElements.forEach(el => timerObserver.observe(el));
  } else {
    setInterval(updateDynamicInfoTimers, 1000);
  }

  const initialCheckedRadios = Array.from(container.querySelectorAll('.js-variant-radio:checked'));
  if (initialCheckedRadios.length > 0) {
      const event = new Event('change', { bubbles: true });
      initialCheckedRadios[0].dispatchEvent(event);
  } else if (variants && variants.length > 0) {
      updateProductUI(variants[0], null, container);
      updateAvailability();
  }

  // Cart API Hijack for Bundled/BOGOF Purchases
  const pickersForCart = container.querySelectorAll('.product-v2__variant-picker');
  const btn = container.querySelector('.addToCartBtn');
  
  if (btn && pickersForCart.length > 1) {
    btn.addEventListener('click', async function() {
      
      const btnLabel = btn.querySelector('.js-atc-label') || btn.querySelector('span');
      const btnPriceWrapper = btn.querySelector('.js-atc-price-wrapper');
      const originalText = btnLabel ? btnLabel.textContent : btn.textContent;
      
      if (btnLabel) btnLabel.textContent = "Adding...";
      else btn.textContent = "Adding...";
      if (btnPriceWrapper) btnPriceWrapper.classList.add('is-hidden');
      btn.disabled = true;

      const v1Options = Array.from(pickersForCart[0].querySelectorAll('.js-variant-radio:checked, input[type="radio"]:checked, select')).map(r => r.value);
      const v2Options = Array.from(pickersForCart[1].querySelectorAll('.js-variant-radio:checked, input[type="radio"]:checked, select')).map(r => r.value);
      
      const v1 = variants.find(v => v1Options.every((val, i) => v.options[i] === val));
      const v2 = variants.find(v => v2Options.every((val, i) => v.options[i] === val));

      if (!v1 || !v2) {
        console.error("Cart API: Variant match failed.");
        if (btnLabel) btnLabel.textContent = originalText;
        else btn.textContent = originalText;
        if (btnPriceWrapper) btnPriceWrapper.classList.remove('is-hidden');
        btn.disabled = false;
        return;
      }

      let items = [];
      if (v1.id === v2.id) {
        items = [{ id: v1.id, quantity: 2 }];
      } else {
        items = [{ id: v1.id, quantity: 1 }, { id: v2.id, quantity: 1 }];
      }

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items })
        });

        if (res.ok) {
          fetch("/cart.js")
            .then(response => response.json())
            .then(cart => {
              if (btnLabel) btnLabel.textContent = originalText;
              else btn.textContent = originalText;
              if (btnPriceWrapper) btnPriceWrapper.classList.remove('is-hidden');
              btn.disabled = false;
            });
        } else {
          const errorData = await res.json();
          console.error(errorData);
          if (btnLabel) btnLabel.textContent = errorData.description || "Out of Stock";
          else btn.textContent = errorData.description || "Out of Stock";
          if (btnPriceWrapper) btnPriceWrapper.classList.add('is-hidden');
        }
      } catch (err) {
        console.error(err);
        if (btnLabel) btnLabel.textContent = originalText;
        else btn.textContent = originalText;
        if (btnPriceWrapper) btnPriceWrapper.classList.remove('is-hidden');
        btn.disabled = false;
      }
    });
  }

  // UI interactions
  const zoomBtn = container.querySelector('.customimagezoom-btn');
  if (zoomBtn) {
    zoomBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const activeSlide = container.querySelector('.product__image-slider .carousel-cell.is-selected');
      if (activeSlide) {
        const activeLightboxLink = activeSlide.querySelector('a.fslightbox');
        if (activeLightboxLink) {
          activeLightboxLink.click();
        }
      }
    });
  }

  const accordions = container.querySelectorAll('.product-accordion');
  accordions.forEach(acc => {
    const titleBtn = acc.querySelector('.product-accordion__title');
    const closeBtn = acc.querySelector('.product-accordion__close-link');
    
    if (titleBtn) {
      titleBtn.addEventListener('click', () => {
        const isOpen = acc.classList.contains('is-open');

        if (isOpen) {
          acc.classList.remove('is-open');
          titleBtn.setAttribute('aria-expanded', 'false');
        } else {
          acc.classList.add('is-open');
          titleBtn.setAttribute('aria-expanded', 'true');
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        
        acc.classList.remove('is-open');
        if (titleBtn) titleBtn.setAttribute('aria-expanded', 'false');
        
        const rect = acc.getBoundingClientRect();
        
        if (rect.top < 100) {
          const elementPosition = rect.top + window.pageYOffset;
          
          window.scrollTo({
               top: elementPosition - 100, 
               behavior: 'smooth'
          });
        }
      });
    }
  });

  // Sister products modal logic
  const sisterTrigger = container.querySelector('.js-sister-popup-trigger');
  const popupContainer = container.querySelector(`#SisterPopup-${sectionId}`);
  
  if (sisterTrigger && popupContainer) {
    const backdrop = popupContainer.querySelector('.sp-backdrop');
    const closeBtn = popupContainer.querySelector('.sp-close-btn');
    const headingTarget = popupContainer.querySelector('.sp-inner-heading');
    const contentTarget = popupContainer.querySelector('.sp-scroll-area');
    
    let contentLoaded = false;
    const focusableElementsString = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    let firstFocusableElement;
    let lastFocusableElement;

    function openPopup(e) {
      e.preventDefault(); 
      const contentUrl = sisterTrigger.getAttribute('href');
      
      if (!contentUrl || contentUrl === '#' || contentUrl === '') return;

      document.body.classList.add('sp-active');
      popupContainer.setAttribute('aria-hidden', 'false');

      const focusableContent = popupContainer.querySelectorAll(focusableElementsString);
      if (focusableContent.length > 0) {
        firstFocusableElement = focusableContent[0];
        lastFocusableElement = focusableContent[focusableContent.length - 1];
      }
      
      if (closeBtn) closeBtn.focus();
      
      if (!contentLoaded) {
        fetch(contentUrl)
          .then(res => res.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            const fetchedHeading = doc.querySelector('.rich-text__heading') || doc.querySelector('h1') || doc.querySelector('.section-header__title');
            const fetchedContent = doc.querySelector('.content-for-layout') || doc.querySelector('main');

            if(headingTarget && fetchedHeading) headingTarget.innerHTML = fetchedHeading.innerText;
            if(contentTarget && fetchedContent) contentTarget.innerHTML = fetchedContent.innerHTML;
            
            const unwantedSections = contentTarget.querySelectorAll('section:nth-child(1), section:nth-child(2)');
            unwantedSections.forEach(el => el.style.display = 'none');

            contentLoaded = true;

            const updatedFocusableContent = popupContainer.querySelectorAll(focusableElementsString);
            if (updatedFocusableContent.length > 0) {
              firstFocusableElement = updatedFocusableContent[0];
              lastFocusableElement = updatedFocusableContent[updatedFocusableContent.length - 1];
            }
          })
          .catch(err => {
            console.error('Error loading comparison content:', err);
            if(contentTarget) contentTarget.innerHTML = '<p>Content could not be loaded.</p>';
          });
      }
    }

    function closePopup() {
      document.body.classList.remove('sp-active');
      popupContainer.setAttribute('aria-hidden', 'true');
      if (sisterTrigger) sisterTrigger.focus();
    }

    popupContainer.addEventListener('keydown', function(e) {
      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
      if (!isTabPressed) return;

      if (e.shiftKey) { 
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else { 
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    });

    sisterTrigger.addEventListener('click', openPopup);
    if (closeBtn) closeBtn.addEventListener('click', closePopup);
    if (backdrop) backdrop.addEventListener('click', closePopup);
    
    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' && document.body.classList.contains('sp-active')) {
        closePopup();
      }
    });
  }

  // Dynamic review scroll tracker
  container.addEventListener('click', function (e) {
    const reviewLink = e.target.closest('.mainproductreviews');
    
    if (reviewLink) {
      e.preventDefault();
      e.stopPropagation();

      let targetSection = document.querySelector('#title-r');

      // Trigger existing tab logic if the review tab header is present
      if (targetSection) {
        targetSection.click();
      }

      if (!targetSection || targetSection.offsetParent === null) {
        targetSection = document.querySelector('[data-oke-widget]');
      }
      
      if (targetSection) {
        const yOffset = -100; 
        const yPosition = targetSection.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({ top: yPosition, behavior: 'smooth' });
      }
    }
  });

  if (document.querySelector('.product-v2__bundle-picker')) {
    class BundleColourPreview {
      constructor(container) {
        this.container = container;
        this.previews = Array.from(container.querySelectorAll('.js-bundle-colour-preview'));
        this.layers = {
          pillowcases: { fallback: '#f2e8df', defaultLabel: 'Pillowcases' },
          fitted_sheet: { fallback: '#e9e4dd', defaultLabel: 'Fitted sheet' },
          flat_sheet: { fallback: '#eef1ee', defaultLabel: 'Flat sheet' },
          quilt_cover: { fallback: '#cbdce4', defaultLabel: 'Quilt cover' }
        };

        if (!this.previews.length) return;

        this.container.addEventListener('change', (event) => {
          if (event.target.matches('.js-bundle-variant-radio, .js-bundle-optional')) this.update();
        });
        this.update();
      }

      update() {
        const selections = {};

        this.container.querySelectorAll('.product-v2__bundle-picker').forEach((picker) => {
          const layer = picker.dataset.previewLayer;
          if (!this.layers[layer]) return;

          const optionalToggle = picker.querySelector('.js-bundle-optional');
          const isIncluded = !optionalToggle || optionalToggle.checked;
          const selectedColour = picker.querySelector('.js-bundle-variant-radio[data-colour-hex]:checked');
          const label = picker.dataset.previewLabel || this.layers[layer].defaultLabel;

          selections[layer] = {
            colour: isIncluded && selectedColour ? selectedColour.dataset.colourHex : this.layers[layer].fallback,
            label,
            value: isIncluded && selectedColour ? selectedColour.value : 'Not included'
          };
        });

        this.previews.forEach((preview) => {
          const description = [];

          Object.keys(this.layers).forEach((layer) => {
            const selection = selections[layer] || {
              colour: this.layers[layer].fallback,
              label: this.layers[layer].defaultLabel,
              value: 'Select a colour'
            };
            const summary = `${selection.label}: ${selection.value}`;

            preview.style.setProperty(`--bundle-${layer.replace(/_/g, '-')}-colour`, selection.colour);
            const swatch = preview.querySelector(`[data-preview-layer-swatch="${layer}"]`);
            const text = preview.querySelector(`[data-preview-selection="${layer}"]`);
            if (swatch) swatch.style.backgroundColor = selection.colour;
            if (text) text.textContent = summary;
            description.push(summary);
          });

          const descriptionElement = preview.querySelector('.js-bundle-preview-description');
          if (descriptionElement) descriptionElement.textContent = `Your selected bedding colours. ${description.join('. ')}.`;
        });
      }
    }

    new BundleColourPreview(container);

    class BundleManager {
      constructor(container) {
        this.container = container;
        this.pickers = Array.from(this.container.querySelectorAll('.product-v2__bundle-picker'));
        this.buyButtonBlock = this.container.querySelector('.product-v2__buy-button-block');
        this.atcButton = this.container.querySelector('.js-bundle-add-to-cart');
        this.priceDisplay = this.container.querySelector('.js-bundle-atc-price');
        
        if (!this.buyButtonBlock || !this.atcButton) return;
        
        this.discountType = this.buyButtonBlock.dataset.discountType;
        this.discountValue = parseFloat(this.buyButtonBlock.dataset.discountValue);

        // How many pieces the customer must keep included, clamped at BOTH ends.
        // Upper: never demand more pieces than the bundle actually has.
        // Lower: never below BUNDLE_APP_MINIMUM. The discount app will not discount a
        // single item, so anything lower lets the page quote a price the cart will not
        // honour. The floor is what keeps that case unreachable - do not relax it.
        const requestedMinimum = parseInt(this.buyButtonBlock.dataset.minimumPieces, 10);
        const BUNDLE_APP_MINIMUM = 2;
        const wanted = Number.isFinite(requestedMinimum) && requestedMinimum > 0 ? requestedMinimum : BUNDLE_APP_MINIMUM;

        if (wanted < BUNDLE_APP_MINIMUM) {
          console.warn(
            `Bundle: minimum_pieces is set to ${wanted}, but the bundle discount needs ` +
            `${BUNDLE_APP_MINIMUM}. Using ${BUNDLE_APP_MINIMUM} so the page cannot quote a ` +
            `price the cart will not honour.`
          );
        }

        this.minimumPieces = Math.min(
          Math.max(wanted, BUNDLE_APP_MINIMUM),
          this.pickers.length
        );
        this.minimumNote = this.container.querySelector('.js-bundle-minimum-note');
        this.dropNote = this.container.querySelector('.js-bundle-drop-note');

        // The one-second tick lives outside the class. Hand it a way back in so an
        // expiring drop re-renders the bundle instead of leaving a dead timer on screen.
        this.container._bundleManager = this;

        this.state = {};
        
        this.init();
      }

      init() {
        this.pickers.forEach(picker => {
          const blockId = picker.dataset.blockId;
          const dataScript = picker.querySelector('.js-bundle-product-data');
          
          if (!dataScript) return;
          
          const variants = JSON.parse(dataScript.textContent);
          const isOptionalToggle = picker.querySelector('.js-bundle-optional');
          
          this.state[blockId] = {
            variants: variants,
            pickerElement: picker,
            images: this.parseVariantImages(picker),
            inventory: this.parseVariantInventory(picker),
            drops: this.parseVariantDrops(picker),
            elements: {
              sub: picker.querySelector('.js-bundle-piece-sub'),
              was: picker.querySelector('.js-bundle-piece-was'),
              now: picker.querySelector('.js-bundle-piece-now'),
              thumb: picker.querySelector('.js-bundle-thumb')
            },
            selectedVariant: null,
            isActive: isOptionalToggle ? isOptionalToggle.checked : true
          };

          // Resolve from the radios that are actually checked rather than variants[0];
          // the two can differ and the per-piece price makes any mismatch visible.
          this.state[blockId].selectedVariant = this.resolveVariant(this.state[blockId]) || variants[0];

          picker.addEventListener('change', (e) => this.handleSelection(e, blockId));
        });

        this.atcButton.addEventListener('click', () => this.submitBundle());
        this.updatePricing();
        this.warmVariantImages();
      }

      // Warm every colourway thumbnail once the page is idle so the first click on a
      // colour paints from cache. Deduplicated by URL, because a piece can carry many
      // variants sharing one image per colourway.
      warmVariantImages() {
        const urls = new Set();

        Object.values(this.state).forEach(block => {
          Object.values(block.images).forEach(src => {
            if (src) urls.add(src);
          });
        });

        if (!urls.size) return;

        const warm = () => urls.forEach(src => {
          const img = new Image();
          img.src = src;
        });

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(warm, { timeout: 2000 });
        } else {
          window.setTimeout(warm, 1200);
        }
      }

      // Variant id to thumbnail source, emitted by the bundle_variant_picker block.
      parseVariantImages(picker) {
        const script = picker.querySelector('.js-bundle-variant-images');
        const map = {};

        if (!script) return map;

        try {
          JSON.parse(script.textContent).forEach(entry => {
            if (entry && entry.src) map[entry.id] = entry.src;
          });
        } catch (err) {
          console.warn('Bundle variant images could not be parsed', err);
        }

        return map;
      }

      // Variant id to inventory policy, emitted by the bundle_variant_picker block.
      // Shopify's `variant | json` omits inventory_policy, so without this map a piece
      // only has a yes or no answer on availability.
      parseVariantInventory(picker) {
        const script = picker.querySelector('.js-bundle-variant-inventory');
        const map = {};

        if (!script) return map;

        try {
          JSON.parse(script.textContent).forEach(entry => {
            if (entry && entry.id) map[entry.id] = entry.policy;
          });
        } catch (err) {
          console.warn('Bundle variant inventory could not be parsed', err);
        }

        return map;
      }

      // Variant id to drop date, emitted by the bundle_variant_picker block. Metafields
      // are absent from `variant | json`, so this map is the only way a piece knows it is
      // an unreleased limited edition.
      parseVariantDrops(picker) {
        const script = picker.querySelector('.js-bundle-variant-drops');
        const map = {};

        if (!script) return map;

        try {
          JSON.parse(script.textContent).forEach(entry => {
            if (entry && entry.id && entry.drop_date) map[entry.id] = entry.drop_date;
          });
        } catch (err) {
          console.warn('Bundle variant drop dates could not be parsed', err);
        }

        return map;
      }

      // The deadline this piece is waiting on, or null if it is available now.
      pieceDropDeadline(blockState) {
        const variant = blockState.selectedVariant;
        if (!variant) return null;
        return futureDropDeadline(blockState.drops[variant.id]);
      }

      // The bundle ships as one parcel, so it is not ready until its LAST included piece
      // has dropped. Excluded pieces are ignored - unticking the limited edition colour
      // is how a customer chooses not to wait.
      bundleDropDeadline(blocks) {
        return blocks.reduce(
          (latest, block) => block.isActive ? laterDeadline(latest, this.pieceDropDeadline(block)) : latest,
          null
        );
      }

      // Buyability, matching the standard product page: in stock, OR out of stock and
      // set to keep selling. The second half is what makes pre-order and backorder
      // possible, and it needs the inventory map above.
      isBuyable(variant, blockState) {
        if (!variant) return false;
        if (variant.available) return true;
        return blockState.inventory[variant.id] === 'continue';
      }

      // Mark size and colour values that cannot be bought in the current combination,
      // scoped to this picker's own radios. The .is-disabled styling lives in
      // main-product-v2.css.
      markUnavailableOptions(blockState) {
        const picker = blockState.pickerElement;
        const fieldsets = Array.from(picker.querySelectorAll('fieldset.product-v2__option-fieldset'));

        const currentSelections = fieldsets.map(fieldset => {
          const checked = fieldset.querySelector('.js-bundle-variant-radio:checked');
          return checked ? checked.value : null;
        });

        fieldsets.forEach((fieldset, optionIndex) => {
          fieldset.querySelectorAll('.js-bundle-variant-radio').forEach(radio => {
            const hypothetical = [...currentSelections];
            hypothetical[optionIndex] = radio.value;

            const match = blockState.variants.find(v =>
              hypothetical.every((val, i) => val === null || v.options[i] === val)
            );

            const label = picker.querySelector(`label[for="${radio.id}"]`);
            if (label) label.classList.toggle('is-disabled', !this.isBuyable(match, blockState));
          });
        });
      }

      // The variant matching every checked radio in this picker, or null if the
      // combination does not exist.
      resolveVariant(blockState) {
        const checkedRadios = Array.from(
          blockState.pickerElement.querySelectorAll('.js-bundle-variant-radio:checked')
        ).map(r => r.value);

        if (!checkedRadios.length) return null;

        return blockState.variants.find(v => checkedRadios.every((val, i) => v.options[i] === val)) || null;
      }

      formatPrice(cents) {
        return cents % 100 === 0 ? (cents / 100).toString() : (cents / 100).toFixed(2);
      }

      handleSelection(e, blockId) {
        const blockState = this.state[blockId];
        
        if (e.target.classList.contains('js-bundle-optional')) {
          blockState.isActive = e.target.checked;
        } else if (e.target.classList.contains('js-bundle-variant-radio')) {
          const fieldset = e.target.closest('fieldset');
          
          if (fieldset) {
            const selectedValueSpan = fieldset.querySelector('.js-selected-value');
            if (selectedValueSpan) selectedValueSpan.textContent = e.target.value;
          }

          blockState.selectedVariant = this.resolveVariant(blockState);
        }
        
        this.updatePricing();
      }

      // At the minimum the included pieces can no longer be unticked, while excluded
      // pieces stay clickable so one can always be added back. The note below the button
      // says why, because a checkbox that silently stops responding reads as broken.
      enforceMinimum(blocks, activeItems) {
        const atMinimum = activeItems <= this.minimumPieces;

        blocks.forEach(block => {
          const toggle = block.pickerElement.querySelector('.js-bundle-optional');
          if (!toggle) return;

          const lock = atMinimum && block.isActive;
          toggle.disabled = lock;
          block.pickerElement.classList.toggle('is-locked', lock);

          if (lock) {
            toggle.setAttribute('title', `Keep at least ${this.minimumPieces} pieces to build a bundle.`);
          } else {
            toggle.removeAttribute('title');
          }
        });

        if (this.minimumNote) {
          this.minimumNote.textContent = atMinimum ? this.minimumNoteText() : '';
          this.minimumNote.classList.toggle('is-hidden', !atMinimum);
        }
      }

      // The note reassures first and invites second. At the minimum the customer already
      // has a complete bundle, so it says so, then offers the extra piece as the way to
      // free one up for swapping. Asked for by Jenny Fang, Wednesday 16 September 2026:
      // "a bundle needs at least 2 pieces" read as a requirement to buy a third.
      // When the minimum is every piece there is nothing to add, so the invitation is
      // dropped rather than pointing at a piece that does not exist.
      minimumNoteText() {
        const settled = `You're set with ${this.minimumPieces} items.`;
        if (this.minimumPieces >= this.pickers.length) return settled;

        const ORDINALS = ['', '', 'a second', 'a third', 'a fourth', 'a fifth', 'a sixth'];
        const next = ORDINALS[this.minimumPieces + 1] || 'another';
        return `${settled} Want to swap? Add ${next}.`;
      }

      // The drop note sits under the button and carries the countdown. Hidden entirely
      // when nothing included is dropping, so an ordinary bundle is unchanged.
      renderDropNote(deadline) {
        if (!this.dropNote) return;
        this.dropNote.classList.toggle('is-hidden', !deadline);
        if (deadline) updateBundleDropTimer();
      }

      // Header row for one piece: include state, thumbnail, size and colour line, and the
      // piece's own price. When the bundle discount is live the full price is struck
      // through above the discounted one.
      renderPiece(blockState, discountActive) {
        const { pickerElement, elements, selectedVariant, isActive } = blockState;

        pickerElement.classList.toggle('is-included', isActive);
        pickerElement.classList.toggle('is-excluded', !isActive);

        if (elements.sub) {
          const chosen = Array.from(
            pickerElement.querySelectorAll('.js-bundle-variant-radio:checked')
          ).map(r => r.value);
          elements.sub.textContent = chosen.join(', ');
        }

        if (elements.thumb && selectedVariant) {
          const src = blockState.images[selectedVariant.id];
          if (src && elements.thumb.getAttribute('src') !== src) {
            elements.thumb.setAttribute('src', src);
          }
        }

        if (!elements.now) return;

        if (!selectedVariant) {
          elements.now.textContent = '';
          elements.now.classList.remove('is-discounted');
          if (elements.was) elements.was.textContent = '';
          return;
        }

        // A price on an unbuyable variant is a promise the cart cannot keep, so the row
        // says so instead of quoting.
        if (!this.isBuyable(selectedVariant, blockState)) {
          elements.now.textContent = 'Unavailable';
          elements.now.classList.remove('is-discounted');
          elements.now.classList.add('is-unavailable');
          if (elements.was) elements.was.textContent = '';
          return;
        }

        elements.now.classList.remove('is-unavailable');

        const full = selectedVariant.price;
        const showDiscount = isActive && discountActive;

        if (showDiscount) {
          // Rounded per piece: the bundle app discounts each cart line, so the per-line
          // figure is what the customer is charged.
          const discounted = Math.round(full * (1 - (this.discountValue / 100)));
          elements.now.textContent = `$${this.formatPrice(discounted)}`;
          elements.now.classList.add('is-discounted');
          if (elements.was) elements.was.textContent = `$${this.formatPrice(full)}`;
        } else {
          elements.now.textContent = `$${this.formatPrice(full)}`;
          elements.now.classList.remove('is-discounted');
          if (elements.was) elements.was.textContent = '';
        }
      }

      updatePricing() {
        let totalPrice = 0;
        let totalComparePrice = 0;
        let allAvailable = true;
        let activeItems = 0;

        const blocks = Object.values(this.state);

        blocks.forEach(block => {
          if (block.isActive) activeItems++;
        });

        // The bundle app enforces a two-item minimum, so a single piece is charged at
        // full price. Only a percentage discount divides across the pieces; a fixed
        // amount comes off the bundle as a whole and cannot be shown per row.
        // This flag governs the per-piece rows only - the bundle total below still
        // discounts from one piece up.
        const perPieceDiscountActive = activeItems >= this.minimumPieces && this.discountType === 'percentage';

        this.enforceMinimum(blocks, activeItems);

        // A limited edition piece the customer has INCLUDED makes the whole bundle a
        // pre-order. Applied once availability is known, so a selection the cart would
        // refuse is not also promised a drop date.
        const bundleDrop = this.bundleDropDeadline(blocks);

        blocks.forEach(block => this.markUnavailableOptions(block));
        blocks.forEach(block => this.renderPiece(block, perPieceDiscountActive));

        blocks.forEach(block => {
          if (!block.isActive) return;
          
          if (!this.isBuyable(block.selectedVariant, block)) {
            allAvailable = false;
          } else {
            totalPrice += block.selectedVariant.price;
            totalComparePrice += (block.selectedVariant.compare_at_price > block.selectedVariant.price) 
                                    ? block.selectedVariant.compare_at_price 
                                    : block.selectedVariant.price;
          }
        });

        const unDiscountedTotal = totalPrice;

        if (activeItems === 0) {
          allAvailable = false;
        } else {
          if (this.discountType === 'percentage') {
            totalPrice = totalPrice * (1 - (this.discountValue / 100));
          } else if (this.discountType === 'fixed') {
            totalPrice = Math.max(0, totalPrice - (this.discountValue * 100));
          }
        }

        // Only a bundle the customer can actually buy gets the pre-order treatment.
        // Stashed on the container so the shared one-second tick can write the
        // countdown without reaching into this class.
        const shownDrop = allAvailable ? bundleDrop : null;
        this.container._bundleDropDeadline = shownDrop;
        this.renderDropNote(shownDrop);

        if (!allAvailable) {
          this.atcButton.disabled = true;
          this.atcButton.classList.remove('preorder');
          this.atcButton.querySelector('.js-bundle-atc-label').textContent = 'SELECTION UNAVAILABLE';
          this.priceDisplay.parentElement.classList.add('is-hidden');
        } else {
          this.atcButton.disabled = false;
          this.atcButton.classList.toggle('preorder', !!shownDrop);
          this.atcButton.querySelector('.js-bundle-atc-label').textContent =
            shownDrop ? 'Pre-Order Bundle' : 'Add Bundle to Cart';
          this.priceDisplay.textContent = `$${(totalPrice / 100).toFixed(2)}`;
          this.priceDisplay.parentElement.classList.remove('is-hidden');
          
          this.updateMainPriceUI(totalPrice, totalComparePrice, unDiscountedTotal);
        }
      }

      updateMainPriceUI(discountedPrice, comparePrice, originalTotal) {
        const formatPrice = (cents) => cents % 100 === 0 ? (cents / 100).toString() : (cents / 100).toFixed(2);
        
        const priceEls = this.container.querySelectorAll('.js-price');
        const saveEl = this.container.querySelector('.js-save-price');
        const compEl = this.container.querySelector('.js-compare-price');
        const saveBadge = this.container.querySelector('.js-save-badge');
        const compareBadge = this.container.querySelector('.js-compare-badge');
        const afterpayEl = this.container.querySelector('.js-afterpay-payments');

        const formattedPriceHTML = `<sup class="price-symbol">$</sup>${formatPrice(discountedPrice)}`;
        priceEls.forEach(el => el.innerHTML = formattedPriceHTML);

        const baseReferencePrice = Math.max(originalTotal, comparePrice);
        
        if (baseReferencePrice > discountedPrice) {
          if (saveEl) saveEl.textContent = `$${formatPrice(baseReferencePrice - discountedPrice)}`;
          if (compEl) compEl.textContent = `$${formatPrice(baseReferencePrice)}`;
          if (saveBadge) saveBadge.classList.remove('is-hidden');
          if (compareBadge) compareBadge.classList.remove('is-hidden');
        } else {
          if (saveBadge) saveBadge.classList.add('is-hidden');
          if (compareBadge) compareBadge.classList.add('is-hidden');
        }

        if (afterpayEl) {
          afterpayEl.textContent = `$${formatPrice(Math.round(discountedPrice / 4))}`;
        }
      }

      async submitBundle() {
        this.atcButton.disabled = true;
        this.atcButton.querySelector('.js-bundle-atc-label').textContent = 'Adding...';
        
        const items = [];

        Object.values(this.state).forEach(block => {
          if (block.isActive && block.selectedVariant) {
            items.push({
              id: block.selectedVariant.id,
              quantity: 1
            });
          }
        });

        if (items.length === 0) return;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          });

          if (res.ok) {
            fetch('/cart.js')
              .then(response => response.json())
              .then(cart => {
                  window.dispatchEvent(new Event('cart:updated'));
                  window.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { cart: cart } }));
                  this.atcButton.disabled = false;
                  this.atcButton.querySelector('.js-bundle-atc-label').textContent = 'Add Bundle to Cart';
              });
          } else {
            const errorData = await res.json();
            console.error(errorData);
            this.atcButton.querySelector('.js-bundle-atc-label').textContent = errorData.description || 'Out of Stock';
          }
        } catch (err) {
          console.error(err);
          this.atcButton.disabled = false;
          this.atcButton.querySelector('.js-bundle-atc-label').textContent = 'Error Adding Bundle';
        }
      }
    }

    new BundleManager(container);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product-v2-container').forEach(initProductV2);
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('product-v2-container')) {
    initProductV2(event.target);
  } else {
    event.target.querySelectorAll('.product-v2-container').forEach(initProductV2);
  }
});
